import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";

// Stripe redirect landing pages. success_url points here with ?payment_id=;
// fulfillment happens through the VERIFIED WEBHOOK, never through this page,
// so all it does is poll the payment status honestly: paid -> celebrate,
// pending -> keep checking (webhooks can lag the redirect by a few seconds),
// cancelled route -> nothing was charged.
const POLL_MS = 2500;
const MAX_POLLS = 12; // ~30s, then show the honest "still processing" state

export default function PaymentResultPage({ variant = "success" }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const paymentId = params.get("payment_id");
  const [payment, setPayment] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const polls = useRef(0);

  const cancelled = variant === "cancelled" || location.pathname.endsWith("/cancelled");

  useEffect(() => {
    if (cancelled || !paymentId) return undefined;
    let alive = true;
    let timer = null;
    async function check() {
      try {
        const res = await api.get("/payments/history");
        const hit = (res.data?.payments || []).find((p) => p.id === paymentId);
        if (!alive) return;
        if (hit) setPayment(hit);
        if (hit?.status === "paid") return; // done, stop polling
      } catch {
        /* transient; the next poll retries */
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        if (alive) setTimedOut(true);
        return;
      }
      timer = setTimeout(check, POLL_MS);
    }
    check();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [cancelled, paymentId]);

  const paid = payment?.status === "paid";
  const product = payment?.product || "";
  const isBoost = product.startsWith("boost");

  return (
    <div className="min-h-screen bg-white">
      <NavTop />
      <main className="mx-auto flex max-w-md flex-col items-center px-6 pb-24 pt-20 text-center">
        {cancelled ? (
          <>
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100 text-brand-ink">
              <XCircle size={30} strokeWidth={1.8} />
            </span>
            <h1 className="mt-5 font-display text-2xl font-semibold tracking-tight text-brand-ink">
              Payment cancelled
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-brand-secondary">
              Nothing was charged. You can try again any time.
            </p>
          </>
        ) : paid ? (
          <>
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
              <CheckCircle2 size={30} strokeWidth={1.8} />
            </span>
            <h1 className="mt-5 font-display text-2xl font-semibold tracking-tight text-brand-ink">
              Payment received
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-brand-secondary">
              {isBoost
                ? "Your boost is active. Your listing is moving up the deck right now."
                : "Your purchase is active on your account."}
            </p>
          </>
        ) : (
          <>
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
              <Clock size={30} strokeWidth={1.8} className={timedOut ? "" : "animate-pulse"} />
            </span>
            <h1 className="mt-5 font-display text-2xl font-semibold tracking-tight text-brand-ink">
              {timedOut ? "Payment processing" : "Confirming your payment"}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-brand-secondary">
              {timedOut
                ? "Stripe confirmed your card but our confirmation is taking longer than usual. Your purchase activates automatically the moment it lands. Check your payment history in a few minutes."
                : "One moment. Stripe is telling us your payment cleared."}
            </p>
          </>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/lister"
            className="rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            Go to your dashboard
          </Link>
          <ButtonGhost onClick={() => (window.location.href = "/")}>Back home</ButtonGhost>
        </div>
      </main>
    </div>
  );
}
